import { Global } from '@emotion/react'
import {
  ColorPrimitiveValues,
  LightThemedColorValues,
  DarkThemeColorValues,
} from './variables'

/**
 * Declares a number of css custom properties for the Gem color system.
 * Primitive color values are set on `:root`.
 * Themed color values are set on any element with the `data-gem-theme` attribute
 * set to `light` or `dark`.
 * Using the `<ThemeProvider>` component from @opallabs/hooks will set up these themed
 * container for you.
 */
const GLOBAL_COLOR_STYLES = {
  ':root': ColorPrimitiveValues,
  '[data-gem-theme=light]': LightThemedColorValues,
  '[data-gem-theme=dark]': DarkThemeColorValues,
}

export const GlobalGemColors = () => <Global styles={GLOBAL_COLOR_STYLES} />

/**
 * Injects a `<style>` tag into the DOM defining the gem color theme styles.
 * To be used for the monolith iframe, where react components need to access the variables
 * but react doesn't have complete control of the DOM. Effectively creates the same
 * stylesheet as `GLOBAL_COLOR_STYLES` above.
 */
export const injectGemColors = () => {
  const styleTag = window.document.createElement('style')
  window.document.head.appendChild(styleTag)

  Object.entries(GLOBAL_COLOR_STYLES).forEach(([selector, variableMap]) => {
    appendStyle({ styleTag, selector, variableMap })
  })
}

const appendStyle = ({
  styleTag,
  selector,
  variableMap,
}: {
  styleTag: HTMLStyleElement
  selector: string
  variableMap: Record<string, string>
}) => {
  if (!styleTag.sheet) {
    console.error(
      `Unable to append style rule for ${selector}, the provided "style" tag has not been appended to the DOM.`
    )
    return
  }

  const variableText = Object.entries(variableMap).reduce(
    (accText, [variable, value]) => {
      accText += `${variable}:${value};`
      return accText
    },
    ''
  )
  styleTag.sheet.insertRule(`${selector} { ${variableText} }`)
}
